import { FormInstance, FormProps } from 'antd/lib/index'
import {
  AccountFirmRole,
  AccountType,
} from '../../../../enums/management-input.enum.ts'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import { UserManagementExternalForm } from './user-management-external-form.tsx'
import { convertPxToNumber } from '../../../../utils/helpers.ts'
import IdeyaTypography from '../../../../common/antd/ideya-typography.tsx'
import { UserManagementInternalForm } from './user-management-internal-form.tsx'
import {
  useCreateUserMutation,
  useUpdateUserMutation,
} from '../../../../apis/user-management/user-management-api.api.ts'
import { useCallback, useEffect, useState } from 'react'
import { getErrorDescription } from '../../../../redux/utils.ts'
import notification from 'antd/es/notification/index'
import message from 'antd/es/message/index'
import Form from 'antd/es/form/index'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Radio from 'antd/es/radio/index'
import Segmented from 'antd/es/segmented/index'
import { IdeyaButton } from '../../../../common/antd/ideya-button.tsx'
import {
  GroupPermission,
  UserListItem,
  UserUpdateRequest,
} from '../../../../apis/user-management/user-management-api.def.ts'
import { ActionUserStatusModal } from '../user-modal/user-action/action-user-status-modal.tsx'
import { useTranslation } from 'react-i18next'
import { selectManagementInput } from '../../../../redux/slices/management-input.slice.ts'

export type UserManagementFormFieldTypes = {
  accountType: AccountType.External | AccountType.Internal
  fullName: string
  email: string
  firmId: string
  firmRole: string
  isTrial: boolean
  groupId?: string
  groupRole?: string
  groupPermission?: GroupPermission
  internalRole?: string
  status?: string
  externalId?: string
}

interface UserManagementFormProps {
  onCancel: () => void
  form: FormInstance<UserManagementFormFieldTypes>
  mode: 'create' | 'edit'
  editUser?: UserListItem
}

export const UserManagementForm: React.FC<UserManagementFormProps> = ({
  onCancel,
  form,
  mode,
  editUser,
}) => {
  const { t } = useTranslation()
  const [activeInactiveModal, setActiveInactiveModal] = useState<boolean>(false)
  const [messageApi, messageContextHolder] = message.useMessage()
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()
  const themeStyle = useAppSelector(selectThemeStyle)
  const [createUser, { isLoading, isSuccess, isError, error, reset }] =
    useCreateUserMutation()

  const [
    updateUser,
    {
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateError,
      reset: updateReset,
    },
  ] = useUpdateUserMutation()

  const managementInput = useAppSelector(selectManagementInput)

  const accountType = Form.useWatch('accountType', form)
  const firmRole = Form.useWatch('firmRole', form)

  const onFinish: FormProps<UserManagementFormFieldTypes>['onFinish'] = (
    values
  ) => {
    createUser(values)
  }

  const onUpdate: FormProps<UserManagementFormFieldTypes>['onFinish'] = async (
    values
  ) => {
    await updateUser({
      id: editUser!.id,
      fullName: values.fullName,
      email: values.email,
      firmId: values.firmId,
      firmRole: values.firmRole,
      isTrial: values.isTrial,
      groupId: values.groupId,
      groupRole: values.groupRole,
      groupPermission: values.groupPermission,
      internalRole: values.internalRole,
      status: values.status,
      externalId: values.externalId,
    }).then(() => {
      onCancel()
    })
  }

  const handleStatusChange = async () => {
    const userUpdateRequest = {
      id: editUser?.id,
      fullName: editUser?.fullName,
      email: editUser?.email,
      firmId: editUser?.firm?.id,
      firmRole: editUser?.firmRole,
      groupId: editUser?.group?.id,
      groupRole: editUser?.groupRole,
      groupPermission: editUser?.groupPermission,
      accountType: editUser?.accountType,
      internalRole: editUser?.internalRole,
      externalId: editUser?.externalId,
      isTrial: editUser?.isTrial,
      status: editUser?.status === 'Active' ? 'Inactive' : 'Active',
    } as UserUpdateRequest

    await updateUser(userUpdateRequest)
    setActiveInactiveModal(false)
    onCancel()
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  const success = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: t('user_management.user_modal.form.success_message'),
    })
  }, [messageApi, t])

  const updateSuccess = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: t('user_management.user_modal.form.update_success_message'),
    })
  }, [messageApi, t])

  const errorNotification = useCallback(
    (description: string) => {
      notificationApi.error({
        message: 'Error',
        description,
        placement: 'topRight',
      })
    },
    [notificationApi]
  )

  const onCloseForm = useCallback(() => {
    form.resetFields()
    onCancel()
  }, [form, onCancel])

  useEffect(() => {
    if (isSuccess) {
      success()
      form.resetFields()
      onCloseForm()
      reset()
    }

    if (isUpdateSuccess) {
      updateSuccess()
      onCloseForm()
      updateReset()
    }
  }, [
    isSuccess,
    form,
    onCloseForm,
    reset,
    success,
    isUpdateSuccess,
    updateReset,
    updateSuccess,
  ])

  useEffect(() => {
    if (isError) {
      const description = getErrorDescription(error)
      errorNotification(description)
      reset()
    }

    if (isUpdateError) {
      const description = getErrorDescription(updateError)
      errorNotification(description)
      updateReset()
    }
  }, [
    isError,
    error,
    reset,
    errorNotification,
    isUpdateError,
    updateError,
    updateReset,
  ])

  // Clear all fields when account type changes
  useEffect(() => {
    if (!editUser && accountType !== undefined) {
      const currentAccountType = form.getFieldValue('accountType')
      form.resetFields()

      form.setFieldsValue({ accountType: currentAccountType })
    }
  }, [editUser, accountType, form])

  return (
    <>
      {messageContextHolder}
      {notificationContextHolder}
      <Form
        form={form}
        layout={'vertical'}
        onFinish={mode === 'create' ? onFinish : onUpdate}
        initialValues={{
          accountType: managementInput.accountType,
        }}
      >
        <Row
          gutter={[
            convertPxToNumber(themeStyle.marginBase),
            convertPxToNumber(themeStyle.marginBase),
          ]}
        >
          <Col span={12}>
            <Form.Item
              label={t('user_management.user_modal.form.account_type')}
              name={'accountType'}
              rules={[
                {
                  required: true,
                  message: t(
                    'user_management.user_modal.form.please_select_account_type'
                  ),
                },
              ]}
            >
              <Segmented
                disabled={mode === 'edit'}
                options={[
                  {
                    value: AccountType.External,
                    label: (
                      <div>{t('user_management.user_modal.form.external')}</div>
                    ),
                  },
                  {
                    value: AccountType.Internal,
                    label: (
                      <div>{t('user_management.user_modal.form.internal')}</div>
                    ),
                  },
                ]}
              />
            </Form.Item>
          </Col>
          {((accountType === AccountType.External &&
            firmRole === AccountFirmRole.Normal) ||
            accountType === AccountType.Internal) && (
            <Col span={12}>
              <Form.Item
                label={t('user_management.user_modal.form.trial_account')}
                name={'isTrial'}
                rules={[
                  {
                    required: true,
                    message: t(
                      'user_management.user_modal.form.please_select_trial_account_option'
                    ),
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value={true}>
                    <IdeyaTypography.Text>
                      {t('common.yes')}
                    </IdeyaTypography.Text>
                  </Radio>
                  <Radio value={false}>
                    <IdeyaTypography.Text>
                      {t('common.no')}
                    </IdeyaTypography.Text>
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          )}
        </Row>
        {accountType === AccountType.External && (
          <UserManagementExternalForm form={form} />
        )}
        {accountType === AccountType.Internal && (
          <UserManagementInternalForm form={form} />
        )}
        <Row
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: themeStyle.marginXS,
            marginTop: themeStyle.marginXXL,
          }}
        >
          {editUser && (
            <IdeyaButton
              ideyaType={''}
              type={'primary'}
              danger
              onClick={() => setActiveInactiveModal(true)}
            >
              {editUser.status === 'Active'
                ? t('user_management.user_modal.form.inactive')
                : t('user_management.user_modal.form.active')}
            </IdeyaButton>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: themeStyle.marginXS,
            }}
          >
            <IdeyaButton ideyaType={'secondary'} onClick={handleCancel}>
              {t('common.cancel')}
            </IdeyaButton>
            <IdeyaButton
              loading={isLoading || isUpdateLoading}
              htmlType={'submit'}
            >
              {t('common.ok')}
            </IdeyaButton>
          </div>
        </Row>
      </Form>
      <ActionUserStatusModal
        activeInactiveModal={activeInactiveModal}
        onSubmit={handleStatusChange}
        onClose={() => setActiveInactiveModal(false)}
      />
    </>
  )
}
